import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { isToolUIPart, type DynamicToolUIPart, type ToolUIPart, type UIMessage } from 'ai';
import { getFriendlyToolLabel } from './tool-labels';
import { ThinkingIndicator } from './ThinkingIndicator';

type ToolInvocationPart = ToolUIPart | DynamicToolUIPart;

type ToolStatus = 'running' | 'done' | 'error';

export function getToolName(part: ToolInvocationPart): string {
  const partType = part.type as string;
  if (partType === 'dynamic-tool') {
    const dynamicPart = part as DynamicToolUIPart;
    return dynamicPart.toolName ?? 'tool';
  }
  return part.type.replace(/^tool-/, '');
}

export function isToolPart(part: UIMessage['parts'][number]): part is ToolInvocationPart {
  if (isToolUIPart(part)) return true;
  return (part.type as string) === 'dynamic-tool';
}

export function getToolStatus(part: ToolInvocationPart): ToolStatus {
  if (part.state === 'input-streaming' || part.state === 'input-available') return 'running';
  if (part.state === 'output-error') return 'error';
  return 'done';
}

const stepRow = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  py: 0.5,
} as const;

const stepText = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  color: '#7e8796',
  lineHeight: 1.4,
} as const;

const reasoningBullet = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: '#020f20',
  flex: '0 0 auto',
  ml: '5px',
  mr: '5px',
} as const;

export function ReasoningStep() {
  return (
    <Box sx={stepRow} data-vy="ai-copilot-step-reasoning">
      <Box sx={reasoningBullet} />
      <Typography sx={stepText}>Thinking</Typography>
    </Box>
  );
}

export function ToolStep({ part }: { part: ToolInvocationPart }) {
  const status = getToolStatus(part);

  if (status === 'running') {
    const activityLabel = getFriendlyToolLabel(getToolName(part));
    return (
      <Box data-vy="ai-copilot-step-tool">
        <ThinkingIndicator activityLabel={activityLabel} />
      </Box>
    );
  }

  if (status === 'error') {
    const label = getFriendlyToolLabel(getToolName(part));
    return (
      <Box sx={stepRow} data-vy="ai-copilot-step-tool">
        <ErrorOutlineIcon sx={{ fontSize: 16, color: '#ef4444' }} />
        <Typography sx={stepText}>{`${label} (failed)`}</Typography>
      </Box>
    );
  }

  return null;
}
